import {
    DocumentChange,
    DocumentPatch,
    ImmerPatchAdapter,
    SubscriptionManager,
    NodeUid,
    getDescendants,
    DocumentService,
    FluxListener,
} from "@buildwithflux/core";
import {Unsubscriber} from "@buildwithflux/shared";

import type {DocumentStore} from "modules/document/state/document";
import {UseFeatureFlagsStore} from "modules/feature_flags/FeatureFlagsStore";

export class DocumentObserver {
    private prevDocumentPatches: DocumentPatch | undefined;
    private subscriptionManager: SubscriptionManager<
        "netNodes" | "bakedNodes" | "elements" | "globalRules",
        DocumentChange
    >;

    constructor(
        private readonly documentService: DocumentService,
        private readonly featureFlags: UseFeatureFlagsStore,
        private readonly immerPatchAdapter: ImmerPatchAdapter,
        private readonly documentStore: DocumentStore,
    ) {
        this.prevDocumentPatches = undefined;
        this.subscriptionManager = new SubscriptionManager();

        this.subscribeToDocumentPatches();
        this.subscribeToPcbNodes((change) => {
            if (change.kind !== "subCollection" || !change.payload.pcbLayoutNodes) return;
            this.subscriptionManager.notify("bakedNodes", change);
        });
    }

    /**
     * Subscribe to changes of any element in Redux
     *
     * Caller should use the return `Unsubscriber` method to clean up the subscription properly
     */
    public subscribeToElements(onChange: (changes: DocumentChange) => void): Unsubscriber {
        return this.subscriptionManager.addSubscription("elements", onChange);
    }

    /**
     * Subscribe to changes of any globalRules in Redux
     *
     * Caller should use the return `Unsubscriber` method to clean up the subscription properly
     */
    public subscribeToGlobalRules(onChange: (changes: DocumentChange) => void): Unsubscriber {
        return this.subscriptionManager.addSubscription("globalRules", onChange);
    }

    /**
     * Subscribe changes of any pcb baked nodes in document service
     *
     * Caller should use the return `Unsubscriber` method to clean up the subscription properly
     */
    public subscribeToBakedNodes(onChange: (changes: DocumentChange) => void): Unsubscriber {
        return this.subscriptionManager.addSubscription("bakedNodes", onChange);
    }

    private subscribeToPcbNodes(callback: FluxListener): Unsubscriber {
        return this.documentService.subscribe({
            target: "allPcbNodes",
            callback,
        });
    }

    /**
     * For an element change, if the change is a removal, this method returns a new change object for pcb nodes, that
     * contains all descendants of the removed element. This ensures systems that rely of subscription events for removal
     * of pcb nodes (eg batched pads) are able to react to removal of pad ancestors (which would not directly trigger a
     * pad removal event).
     */
    private getDescendantForRemovalFromElementChanges(changes: Record<"remove", NodeUid[] | undefined>) {
        // Initially only used while batched pads are enabled, since batching is primarily event driven - the extra
        // work being done here is not needed for non-batch pads, or pcb editor without batching enabled.

        // Note: !enablePcbBatchedPads flag used to early exit here

        if ((changes.remove?.length ?? 0) === 0) {
            return;
        }

        const {pcbLayoutNodes} = this.documentService.snapshot();

        const remove = new Set(changes.remove ?? []);

        // eslint-disable-next-line @buildwithflux/no-array-foreach
        changes.remove?.forEach((nodeUid: NodeUid) => {
            const removeNode = pcbLayoutNodes[nodeUid];
            if (!removeNode) {
                return;
            }

            for (const node of getDescendants(pcbLayoutNodes, removeNode)) {
                remove.add(node.uid);
            }
        });

        return {
            remove: Array.from(remove),
        };
    }

    private subscribeToDocumentPatches(): () => void {
        return this.documentStore.subscribe(() => {
            const documentPatches = this.documentStore.getState().document?.latestPatch;

            if (!!documentPatches && documentPatches !== this.prevDocumentPatches) {
                const elementsChanges = this.immerPatchAdapter.toDocumentCollectionChanges(documentPatches).elements;
                const globalRulesChanges =
                    this.immerPatchAdapter.toDocumentCollectionChanges(documentPatches).pcbLayoutRuleSets;

                if (elementsChanges) {
                    const pcbNodeUids = this.getDescendantForRemovalFromElementChanges(elementsChanges);

                    this.subscriptionManager.notify("elements", {
                        kind: "subCollection",
                        payload: {
                            elements: elementsChanges,
                        },
                    });

                    this.subscriptionManager.notify("bakedNodes", {
                        kind: "subCollection",
                        payload: {
                            pcbLayoutNodes: pcbNodeUids,
                        },
                    });
                }

                if (globalRulesChanges) {
                    this.subscriptionManager.notify("globalRules", {
                        kind: "subCollection",
                        payload: {
                            pcbLayoutRuleSets: globalRulesChanges,
                        },
                    });
                }
                this.prevDocumentPatches = documentPatches;
            }
        });
    }
}
